import { registerPlugin } from '@capacitor/core'
import { isNativePlatform } from '@/platform/platform'

export type SecureStoreKey = 'token' | 'deviceId' | 'userId' | 'workspaceId' | 'endpoint' | 'fallbackEndpoint'

interface NativeSecureStorePlugin {
  setItem(options: { key: string; value: string }): Promise<void>
  getItem(options: { key: string }): Promise<{ value: string | null; corrupted?: boolean }>
  removeItem(options: { key: string }): Promise<void>
  clear(): Promise<void>
}

const NativeSecureStore = registerPlugin<NativeSecureStorePlugin>('SecureStore')

// Web fallback: authentication relies on the HttpOnly cookie, so values live in memory only.
const webValues = new Map<string, string>()
let corruptedListener: (() => void) | null = null

export function onSecureStoreCorrupted(listener: () => void) {
  corruptedListener = listener
}

export const secureStore = {
  async setItem(key: SecureStoreKey, value: string) {
    if (isNativePlatform()) {
      await NativeSecureStore.setItem({ key, value })
      return
    }
    webValues.set(key, value)
  },
  async getItem(key: SecureStoreKey): Promise<string | null> {
    if (isNativePlatform()) {
      const result = await NativeSecureStore.getItem({ key })
      if (result.corrupted) {
        // Keystore key was invalidated; storage was wiped natively. Force a safe logout.
        corruptedListener?.()
        return null
      }
      return result.value
    }
    return webValues.get(key) ?? null
  },
  async removeItem(key: SecureStoreKey) {
    if (isNativePlatform()) {
      await NativeSecureStore.removeItem({ key })
      return
    }
    webValues.delete(key)
  },
  async clear() {
    if (isNativePlatform()) {
      await NativeSecureStore.clear()
      return
    }
    webValues.clear()
  },
}
